import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { n8nService } from '../components/core/services/n8n.service';
import { deriveISRSubAction } from '../utils/isrSubAction';
import { rememberIsrSave } from '../utils/isrPendingWrites';
import {
  isIdentifier,
  isSalesField,
  leadRecordMatches,
  normalizeIdentifier,
  normalizeSalesStatus,
  patchCampaignListData,
  patchDetailData,
  readHydratedCompanyId,
  readHydratedPersonId,
  toWireSalesStatus,
} from '../utils/isrLeadCachePatch';
import { queryKeys } from '../services/query/queryKeys';
import type {
  ISRUpdateMetadata,
  ISRUpdateSnapshot,
  MappedLeadData,
} from '../types';

export interface UseISRLeadMutationOptions {
  campaignId: string;
  lvl2Id: string | number | null | undefined;
  userEmail: string | null | undefined;
  /** Actor identity forwarded to the n8n proxy alongside status writes. */
  userId?: string | null;
  userName?: string | null;
  lead: MappedLeadData | null | undefined;
}

export interface ISRLeadMutationVariables {
  snapshot: ISRUpdateSnapshot;
  metadata?: ISRUpdateMetadata;
}

type ISRLeadMutationResponse = Awaited<ReturnType<typeof n8nService.updateISRLead>>;

interface CachedQuerySnapshot {
  queryKey: QueryKey;
  data: unknown;
}

interface ISRMutationContext {
  hadDetailQuery: boolean;
  previousDetail: unknown;
  previousCampaignLists: CachedQuerySnapshot[];
}

// Patch functions and field maps live in utils/isrLeadCachePatch — shared
// with utils/isrPendingWrites so refetch-time defense and save-time optimism
// can never drift apart.

const isStatusMutation = (
  variables: ISRLeadMutationVariables,
  lead: MappedLeadData | null | undefined,
): boolean => {
  const changedFields = variables.metadata?.changed_fields;
  if (changedFields !== undefined) {
    return changedFields.some(isSalesField);
  }

  const nextStatus = variables.snapshot.lead_status;
  if (nextStatus === null) return false;

  const currentStatus = normalizeSalesStatus(lead?.status);
  const currentSubstatus = lead?.salesSubstatus ?? null;

  return currentStatus !== nextStatus || currentSubstatus !== variables.snapshot.lead_substatus;
};

export const useISRLeadMutation = ({
  campaignId,
  lvl2Id,
  userEmail,
  userId,
  userName,
  lead,
}: UseISRLeadMutationOptions) => {
  const queryClient = useQueryClient();
  const personId = readHydratedPersonId(lead);
  const companyId = readHydratedCompanyId(lead);
  const hasRequestIdentity =
    campaignId.trim().length > 0 &&
    isIdentifier(lvl2Id) &&
    typeof userEmail === 'string' &&
    userEmail.trim().length > 0;
  const canMutate = hasRequestIdentity && isIdentifier(personId) && isIdentifier(companyId);
  const detailKey = queryKeys.leads.detail(campaignId, String(lvl2Id ?? ''));
  const campaignListsKey = ['leads', 'campaign', campaignId] as const;

  const mutation = useMutation<
    ISRLeadMutationResponse,
    Error,
    ISRLeadMutationVariables,
    ISRMutationContext
  >({
    mutationKey: ['isr-lead', 'update', campaignId, String(lvl2Id ?? '')],
    mutationFn: async ({ snapshot, metadata }) => {
      if (!hasRequestIdentity || !isIdentifier(lvl2Id) || !userEmail) {
        throw new Error('The SDR workspace requires campaign, lead, and user identifiers before saving.');
      }

      // update_campaign is the SINGLE update action — all ISR changes
      // (contact fields, status/substatus, notes) go through it, the same way
      // status and notes saves already worked. The `updated` payload carries
      // personLvl2/company/notes/status diffs so n8n knows exactly what to write.
      const changedFields = metadata?.changed_fields;
      const hasSalesChange = changedFields !== undefined
        ? changedFields.some(isSalesField)
        : isStatusMutation({ snapshot, metadata }, lead);

      // Anything that touches ONLY status/substatus/notes is exempt from the
      // canonical person/company requirement — notes and status live on the
      // lead's campaign timeline, not the canonical lvl1 rows.
      const isCampaignOwnedOnly =
        changedFields !== undefined &&
        changedFields.length > 0 &&
        changedFields.every((field) => isSalesField(field) || field.trim().toLowerCase() === 'notes');

      // Contact-field edits (personLvl2 / company) still need the hydrated
      // canonical records to resolve which lvl1 rows to update.
      if (!isCampaignOwnedOnly) {
        if (!canMutate) {
          throw new Error('The SDR workspace requires hydrated person, company, and lead identifiers before saving contact fields.');
        }
        if (
          normalizeIdentifier(snapshot.person.id) !== normalizeIdentifier(personId) ||
          normalizeIdentifier(snapshot.company.id) !== normalizeIdentifier(companyId)
        ) {
          throw new Error('The SDR snapshot does not belong to the hydrated lead record.');
        }
      }

      const campaignResponse = await n8nService.updateCampaign({
        campaign_id: campaignId,
        lvl2_id: String(lvl2Id),
        lead_status: hasSalesChange ? toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus) : undefined,
        lead_sub_status: hasSalesChange ? (snapshot.lead_substatus ?? undefined) : undefined,
        notes: snapshot.notes,
        icp_score: lead?.icpScore,
        client_email: userEmail,
        client_id: userId || undefined,
        client_name: userName || undefined,
        updated: metadata?.updated,
        // Canonical lvl1 row targets — resolved from the hydrated lead; absent
        // on sales-only saves where no lvl1 records are required.
        person_lvl1_id: normalizeIdentifier(personId) ?? undefined,
        company_lvl1_id: normalizeIdentifier(companyId) ?? undefined,
        // Single routing hint for the workflow (contact_details | lead_status |
        // notes | snapshot) — see utils/isrSubAction.ts for the priority rules.
        sub_action: deriveISRSubAction(changedFields),
      });

      if (!campaignResponse.success) {
        throw new Error(campaignResponse.error || 'Failed to update the campaign');
      }

      return campaignResponse;
    },
    onMutate: async (variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey, exact: true }),
        queryClient.cancelQueries({ queryKey: campaignListsKey }),
      ]);

      const previousDetail = queryClient.getQueryData(detailKey);
      const previousCampaignLists = queryClient
        .getQueriesData<unknown>({ queryKey: campaignListsKey })
        .map(([queryKey, data]) => ({ queryKey, data }));
      const updatedAt = new Date().toISOString();

      queryClient.setQueryData(detailKey, (current: unknown) =>
        patchDetailData(current, lead, lvl2Id as string | number, variables.snapshot, updatedAt)
      );
      queryClient.setQueriesData<unknown>({ queryKey: campaignListsKey }, (current) =>
        patchCampaignListData(current, lvl2Id as string | number, variables.snapshot, updatedAt)
      );

      return {
        hadDetailQuery: previousDetail !== undefined,
        previousDetail,
        previousCampaignLists,
      };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      if (context.hadDetailQuery) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      } else {
        queryClient.removeQueries({ queryKey: detailKey, exact: true });
      }

      for (const cachedQuery of context.previousCampaignLists) {
        if (cachedQuery.data === undefined) {
          queryClient.removeQueries({ queryKey: cachedQuery.queryKey, exact: true });
        } else {
          queryClient.setQueryData(cachedQuery.queryKey, cachedQuery.data);
        }
      }
    },
    onSuccess: (response, variables) => {
      if (!isIdentifier(lvl2Id)) return;
      const updatedAt = response.timestamp || new Date().toISOString();

      queryClient.setQueryData(detailKey, (current: unknown) =>
        patchDetailData(current, lead, lvl2Id, variables.snapshot, updatedAt)
      );
      queryClient.setQueriesData<unknown>({ queryKey: campaignListsKey }, (current) =>
        patchCampaignListData(current, lvl2Id, variables.snapshot, updatedAt)
      );

      // Register the save so any refetch landing INSIDE the n8n→DB sync
      // window (minutes) defends these values instead of bouncing the user's
      // edits back to the stale server copy. The registry self-clears when
      // the server observes the snapshot or the TTL lapses.
      rememberIsrSave(campaignId, lvl2Id, variables.snapshot);

      // Reload after ANY successful save so the UI converges on server truth
      // and never shows stale info next to a database that already has the new
      // value. Safe against the n8n→DB sync window: the refetches run through
      // useLeadDetail/leads_cards fetchers, whose queryFns re-apply the saved
      // snapshot via applyPendingIsrWrites / applyPendingIsrWritesToCard while
      // the server still lags (10 min TTL, self-clearing). The query caches
      // were already optimistically patched above, so this refetch only
      // CONFIRMS — the user never sees a flicker or a stale value.
      void queryClient.invalidateQueries({ queryKey: detailKey });
      void queryClient.invalidateQueries({ queryKey: campaignListsKey });
    },
  });

  return {
    ...mutation,
    canMutate,
    /** True when the sales stepper can save: campaign + lead + user identity
     *  are enough — the canonical person/company rows are NOT required. */
    canChangeSalesState: hasRequestIdentity,
  };
};

// Back-compat re-export — CampaignIndex imports the wire mapper from here.
export { toWireSalesStatus } from '../utils/isrLeadCachePatch';
